import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { TouchableOpacity, ViewStyle, ActivityIndicator } from 'react-native';

import { useTheme } from '@/components/theme-provider';
import type { IconName } from '@/types/icons';

export interface FABProps {
	icon?: IconName | string;
	onPress?: () => void;
	disabled?: boolean;
	loading?: boolean;
	style?: ViewStyle;
	size?: 'small' | 'medium' | 'large';
	label?: string;
	testID?: string;
}

export const FAB: React.FC<FABProps> = ({
	icon = 'plus',
	onPress,
	disabled = false,
	loading = false,
	style,
	size = 'medium',
	label,
	testID,
}) => {
	const theme = useTheme();

	const getSize = () => {
		switch (size) {
			case 'small':
				return { width: 40, height: 40, iconSize: 20 };
			case 'large':
				return { width: 64, height: 64, iconSize: 32 };
			case 'medium':
			default:
				return { width: 56, height: 56, iconSize: 24 };
		}
	};

	const { width, height, iconSize } = getSize();

	const fabStyle: ViewStyle = {
		width,
		height,
		borderRadius: width / 2,
		backgroundColor: disabled ? theme.colors.outline : theme.colors.primary,
		justifyContent: 'center',
		alignItems: 'center',
		position: 'absolute',
		bottom: 16,
		right: 16,
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 4 },
		shadowOpacity: 0.3,
		shadowRadius: 4.65,
		elevation: 8,
	};

	return (
		<TouchableOpacity
			testID={testID}
			style={[fabStyle, style]}
			onPress={onPress}
			disabled={disabled || loading}
			activeOpacity={0.7}
			accessibilityRole="button"
			accessibilityLabel={label || 'Floating Action Button'}
			accessibilityState={{ disabled: disabled || loading }}
		>
			{loading ? (
				<ActivityIndicator
					size="small"
					color={theme.colors.onPrimary}
				/>
			) : (
				<Ionicons
					name={icon as IconName}
					size={iconSize}
					color={theme.colors.onPrimary}
				/>
			)}
		</TouchableOpacity>
	);
};
